/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { deleteObject, getStorage, ref } from '@angular/fire/storage';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { Timer } from '@touchplan/common';
import { fancyFirebaseGetters, fancyFirebaseReferencer } from '@touchplan/fancy-referencer';
import * as appStrings from '@touchplan/strings';
import { environment } from 'environment';
import firebase from 'firebase/compat/app';
import { delay, from, map, of, retry, switchMap, take, throwError } from 'rxjs';
import { CloudStorageService } from 'src/app/services/cloud-storage.service';
import { FirebaseCloudFunctionsService } from 'src/app/services/firebase-cloud-functions/cloud-functions.service';

const MAX_RETRIES = 3;
const DELAY_MS = 1500;
const ERR_AUTH_ENDPOINT_RATE_LIMIT_REACHED = 'auth/too-many-requests';
const EXAMPLE_EMAIL_ADDRESS = 'test@example.com';

export interface ITest {
	description: string;
	status: 'running' | 'success' | 'failure';
	duration?: number;
}

const REQUIRED_URLS = [
	'my.touchplan.com',
	'touchplan.firebaseio.com',
	'securetoken.googleapis.com',
	'firebasestorage.googleapis.com',
	'us-central1-firebase-touchplan.cloudfunctions.net',
	'fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu4mxKKTU1Kg.woff2',
];

@Component({
	standalone: true,
	selector: 'test',
	templateUrl: './test.component.html',
	styleUrls: ['./test.component.scss'],
	imports: [CommonModule, MatTableModule, MatProgressSpinnerModule],
})
export class TestComponent implements OnInit {
	public title = appStrings.STATUS_PAGE_TITLE;
	public tests: ITest[] = [];
	public displayedColumns: string[] = ['position', 'description', 'duration', 'status'];
	public columnsToDisplay: string[] = this.displayedColumns.slice();

	constructor(
		private angularFireAuth: AngularFireAuth,
		private firebaseCloudFunctionsService: FirebaseCloudFunctionsService,
		private cloudStorageService: CloudStorageService
	) {}

	public ngOnInit() {
		this.readSmallValueFromFirebase();
		this.readLargeDataFromFirebase();
		this.writeToFirebase();
		this.checkForAuthApi();
		this.checkForAuth();
		this.checkCloudFuncConnectivity();
		this.checkCloudFuncConnectivityAuth();
		this.checkRequiredURLs();
		this.checkUploadAndDownloadDocumentsFromGCS();
	}

	private readSmallValueFromFirebase() {
		const duration = new Timer();
		const clientStatusTestsPath = fancyFirebaseGetters.getClientStatusTestsPath();
		const ref = fancyFirebaseReferencer
			.getLegacyRef<firebase.database.Reference>(clientStatusTestsPath)
			.child('readTests')
			.child('smallValue');
		const test: ITest = { description: appStrings.TESTS_BIT_DATA_DESCRIPTION, status: 'running' };
		this.tests.push(test);
		duration.init();
		from(ref.once('value'))
			.pipe(take(1))
			.subscribe({
				next: (snap) => {
					test.status = snap.exists() && snap.val() === 'hi' ? 'success' : 'failure';
					test.duration = duration.end();
				},
				error: () => {
					test.status = 'failure';
					test.duration = duration.end();
				},
			});
	}

	private readLargeDataFromFirebase() {
		const duration = new Timer();
		const clientStatusTestsPath = fancyFirebaseGetters.getClientStatusTestsPath();
		const ref = fancyFirebaseReferencer
			.getLegacyRef<firebase.database.Reference>(clientStatusTestsPath)
			.child('readTests')
			.child('largeValue');
		const test: ITest = { description: appStrings.TESTS_BIG_DATA_DESCRIPTION, status: 'running' };
		this.tests.push(test);
		duration.init();
		from(ref.once('value'))
			.pipe(take(1))
			.subscribe({
				next: (snap) => {
					test.status = snap.exists() ? 'success' : 'failure';
					test.duration = duration.end();
				},
				error: () => {
					test.status = 'failure';
					test.duration = duration.end();
				},
			});
	}

	private writeToFirebase() {
		const duration = new Timer();
		const clientStatusTestsPath = fancyFirebaseGetters.getClientStatusTestsPath();
		const ref = fancyFirebaseReferencer
			.getLegacyRef<firebase.database.Reference>(clientStatusTestsPath)
			.child('writeTests')
			.child('pointA');
		const test: ITest = { description: appStrings.TESTS_WRITING_DATA_DESCRIPTION, status: 'running' };
		this.tests.push(test);
		duration.init();
		from(ref.set('value'))
			.pipe(take(1))
			.subscribe({
				next: () => {
					test.status = 'success';
					test.duration = duration.end();
				},
				error: () => {
					test.status = 'failure';
					test.duration = duration.end();
				},
			});
	}

	private checkForAuthApi() {
		const test: ITest = { description: appStrings.TESTS_CHECKING_AUTH_API, status: 'running' };
		this.tests.push(test);

		const duration = new Timer();
		duration.init();

		from(this.angularFireAuth.fetchSignInMethodsForEmail(EXAMPLE_EMAIL_ADDRESS))
			.pipe(
				take(1),
				retry({
					delay: (err, retryCount) => {
						if (retryCount < MAX_RETRIES && err?.code === ERR_AUTH_ENDPOINT_RATE_LIMIT_REACHED) {
							return of().pipe(delay(DELAY_MS));
						} else {
							return throwError(() => err);
						}
					},
				})
			)
			.subscribe({
				// So long as this subscription doesn't fail, that means the Firebase Auth endpoint is reachable
				next: () => {
					test.status = 'success';
					test.duration = duration.end();
				},
				// Error occurred, Firebase Auth is not reachable
				error: () => {
					test.status = 'failure';
					test.duration = duration.end();
				},
			});
	}

	private checkForAuth() {
		const test: ITest = { description: appStrings.TESTS_CHECKING_AUTH_DESCRIPTION, status: 'running' };
		this.tests.push(test);

		const duration = new Timer();
		duration.init();

		this.angularFireAuth.authState.pipe(take(1)).subscribe({
			next: (user) => {
				test.status = user ? 'success' : 'failure';
				test.duration = duration.end();
			},
			error: () => {
				test.status = 'failure';
				test.duration = duration.end();
			},
		});
	}

	private checkCloudFuncConnectivity() {
		const duration = new Timer();
		const test: ITest = { description: appStrings.TESTS_CLOUD_FUNCTION_DESCRIPTION, status: 'running' };
		this.tests.push(test);
		duration.init();
		this.firebaseCloudFunctionsService
			.checkCloudConnectivity()
			.pipe(take(1))
			.subscribe({
				next: () => {
					test.status = 'success';
					test.duration = duration.end();
				},
				error: () => {
					test.status = 'failure';
					test.duration = duration.end();
				},
			});
	}

	private checkCloudFuncConnectivityAuth() {
		const duration = new Timer();
		const test: ITest = { description: appStrings.TESTS_CLOUD_AUTH_FUNCTION_DESCRIPTION, status: 'running' };
		this.tests.push(test);
		duration.init();
		this.firebaseCloudFunctionsService
			.checkCloudConnectivityAuth()
			.pipe(take(1))
			.subscribe({
				next: () => {
					test.status = 'success';
					test.duration = duration.end();
				},
				error: () => {
					test.status = 'failure';
					test.duration = duration.end();
				},
			});
	}

	private checkRequiredURLs() {
		REQUIRED_URLS.map((url) => {
			const duration = new Timer();
			const test: ITest = { description: url, status: 'running' };
			this.tests.push(test);
			duration.init();
			from(fetch(`https://${url}`, { mode: 'no-cors' }))
				.pipe(take(1))
				.subscribe({
					next: () => {
						test.status = 'success';
						test.duration = duration.end();
					},
					error: () => {
						test.status = 'failure';
						test.duration = duration.end();
					},
				});
		});
	}

	// This test does a few things that must be done sequentially:
	// - Upload a small JSON file
	// - Fetch that JSON file using the requisite methods from `CloudStorageService`
	// - Clean up and delete that uploaded file
	//
	// All of those tasks need to be done sequentially, thus they're stuffed into
	// one big observable chain.
	private checkUploadAndDownloadDocumentsFromGCS() {
		const duration = new Timer();
		const test: ITest = { description: appStrings.TESTS_GCS_ACCESS_PERMISSIONS, status: 'running' };
		this.tests.push(test);
		duration.init();

		let filePath = 'testing';
		const file = new Blob([JSON.stringify({ a: 'b' })], { type: 'application/json' });
		this.angularFireAuth.user
			.pipe(
				take(1),
				switchMap(({ uid: fileName }) =>
					this.cloudStorageService
						.uploadFile$(
							file,
							{
								contentType: 'application/json',
								contentEncoding: 'utf-8',
							},
							fileName,
							filePath,
							environment.firebase.storageBucket
						)
						.pipe(map(() => fileName))
				),
				switchMap((fileName) => {
					const storage = getStorage(firebase.app(), environment.firebase.storageBucket);
					const uploadedFileRef = ref(storage, `${environment.refUrl}${filePath}/${fileName}`);
					return from(
						this.cloudStorageService.getCloudURL(
							`${environment.refUrl}${filePath}/${fileName}`,
							environment.firebase.storageBucket
						)
					).pipe(
						switchMap((downloadUrl) => from(fetch(downloadUrl, { mode: 'no-cors' }))),
						map(() => uploadedFileRef)
					);
				}),
				switchMap((uploadedFileRef) => from(deleteObject(uploadedFileRef)))
			)
			.subscribe({
				next: () => {
					test.status = 'success';
					test.duration = duration.end();
				},
				error: (err) => {
					console.log('Error in Cloud Storage access check', err);
					test.status = 'failure';
					test.duration = duration.end();
				},
			});
	}
}
